import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { AnimatedCounter } from "@/components/ui/AnimatedCounter";

export const AboutSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="sobre"
      ref={ref}
      className="min-h-screen flex items-center py-16 md:py-32 bg-darkblue relative overflow-hidden snap-start"
    >
      {/* Background dinâmico: Glow lateral e gradiente de profundidade */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[20%] right-[-5%] w-[500px] h-[500px] bg-gold opacity-[0.04] rounded-full blur-[120px]" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
      </div>

      <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-24 items-center">
          {/* Conteúdo: Título com tracking largo e linha de gradiente lateral */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center gap-4 mb-6">
              <span className="text-gold text-[10px] uppercase tracking-[0.4em] font-mono">
                Sobre Nós
              </span>
              <div className="h-px w-24 bg-gradient-to-r from-gold/50 to-transparent" />
            </div>

            <h2 className="font-heading text-white text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-8 max-w-[12em]">
              Mais de uma década{" "}
              <span className="text-gold/90">transformando empresas</span>
            </h2>

            <div className="space-y-4 text-slate-300 text-base md:text-lg leading-relaxed">
              <p>
                A APS Administrativo nasceu com o propósito de oferecer soluções
                administrativas completas e personalizadas para empresas que
                buscam excelência em sua gestão.
              </p>
              <p>
                Nossa equipe especializada trabalha lado a lado com nossos
                clientes, entendendo suas necessidades específicas e
                desenvolvendo estratégias eficientes para otimizar processos.
              </p>
            </div>

            {/* Divisor em h-px com gradiente para finalização de bloco */}
            <div className="w-24 h-px bg-gradient-to-r from-gold to-transparent mt-8" />
          </motion.div>

          {/* Stats: Cards com efeito Glow, Spotlight e bordas dinâmicas no hover */}
          <div className="grid gap-6">
            {[
              { end: 10, label: "Anos de Atuação", delay: 0.2 },
              { end: 50, label: "Contratos Atendidos", delay: 0.4 },
            ].map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: 20 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6, delay: stat.delay }}
                className="relative p-10 bg-white/[0.02] border border-gold/10 backdrop-blur-sm overflow-hidden group transition-all duration-500 hover:border-gold/30"
              >
                {/* Efeito Spotlight: Luz de preenchimento no hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-gold/[0.05] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                {/* Glow de Canto e moldura de luz (brilho premium) */}
                <div className="absolute -top-12 -right-12 w-24 h-24 bg-gold/20 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute top-0 right-0 w-16 h-px bg-gold opacity-0 group-hover:opacity-100 transition-all duration-500 shadow-[0_0_20px_#D4AF37]" />
                <div className="absolute top-0 right-0 w-px h-16 bg-gold opacity-0 group-hover:opacity-100 transition-all duration-500 shadow-[0_0_20px_#D4AF37]" />

                <div className="relative z-10">
                  <AnimatedCounter
                    end={stat.end}
                    label={stat.label}
                    isInView={isInView}
                  />
                </div>

                {/* Linha final: Gradiente de subida com Glow expansivo */}
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-[2px] bg-gold opacity-0 group-hover:w-full group-hover:opacity-100 transition-all duration-700 shadow-[0_0_15px_#D4AF37]" />
                <div className="absolute bottom-0 left-0 w-full h-px bg-white/5" />
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
